import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Send, Loader2, Scissors, ShieldCheck } from 'lucide-react';

import Modal from '../ui/Modal';
import PrimaryButton from '../ui/PrimaryButton';

import { useUsers } from '@/contexts/UsersContext';
import { toast } from 'sonner';
import { validateEmail } from '@/lib/validator';

interface NewStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewStaffModal: React.FC<NewStaffModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { inviteUser } = useUsers();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const [isInviting, setIsInviting] = useState(false);

  const handleSubmit = async () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      toast.error('Email richiesta');
      return;
    }

    const emailValidation = validateEmail(trimmedEmail);
    if (!emailValidation.isValid) {
      toast.error(emailValidation.message);
      return;
    }

    setIsInviting(true);
    try {
      const result = await inviteUser(trimmedEmail, role);

      if (!result.success) {
        toast.error(result.error || 'Errore durante l’invio dell’invito');
      } else {
        toast.success('Invito inviato con successo!');
        setEmail('');
        setRole('user');
        onClose();
      }
    } catch (error) {
      toast.error('Errore durante l’invio dell’invito');
      console.error(error);
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isInviting ? () => { } : onClose}
      headerContent={
        <div className="space-y-1">
          <h2 className="text-2xl font-bold text-gray-900">
            {t('staff.invite_modal.title')}
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            {t('staff.invite_modal.subtitle')}
          </p>
        </div>
      }
    >
      <div className="space-y-1">
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
            <Mail size={14} /> {t('staff.invite_modal.email_label')} *
          </label>
          <input
            type="email"
            placeholder="collaboratore@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 outline-none font-medium text-gray-900"
            disabled={isInviting}
          />
          <p className="text-xs text-gray-400">
            {t('staff.invite_modal.helper')}
          </p>
        </div>

        {/* Ruolo assegnato all'accettazione dell'invito */}
        <div className="space-y-2 pt-4">
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">
            {t('staff.invite_modal.role_label', 'Ruolo')} *
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setRole('user')}
              disabled={isInviting}
              className={`flex flex-col items-start gap-1 p-3 rounded-xl border text-left transition-colors ${role === 'user' ? 'bg-clip-50 border-clip-300 ring-1 ring-clip-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
            >
              <span className="flex items-center gap-2 text-sm font-bold text-gray-900">
                <Scissors size={14} className="text-clip-500" />
                {t('staff.invite_modal.role_user', 'Collaboratore')}
              </span>
              <span className="text-[11px] text-gray-500">
                {t('staff.invite_modal.role_user_desc', 'Gestisce la propria agenda e i propri appuntamenti.')}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setRole('admin')}
              disabled={isInviting}
              className={`flex flex-col items-start gap-1 p-3 rounded-xl border text-left transition-colors ${role === 'admin' ? 'bg-clip-50 border-clip-300 ring-1 ring-clip-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
            >
              <span className="flex items-center gap-2 text-sm font-bold text-gray-900">
                <ShieldCheck size={14} className="text-clip-500" />
                {t('staff.invite_modal.role_admin', 'Amministratore')}
              </span>
              <span className="text-[11px] text-gray-500">
                {t('staff.invite_modal.role_admin_desc', 'Accesso completo: analytics, staff, impostazioni.')}
              </span>
            </button>
          </div>
        </div>

        <div className="pt-6 mt-2 border-t border-gray-100 flex gap-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-colors"
            disabled={isInviting}
          >
            {t('common.cancel')}
          </button>
          <PrimaryButton
            label={isInviting ? 'Invio in corso...' : t('staff.invite_modal.send_invite')}
            icon={isInviting ? Loader2 : Send}
            className="flex-1 justify-center"
            onClick={handleSubmit}
            disabled={!email || isInviting}
          />
        </div>
      </div>
    </Modal>
  );
};

export default NewStaffModal;